---
title: "Net revenue retention is the only number that *compounds*."
excerpt: "New logos add linearly. Retained and expanding revenue compounds. NRR is the one metric that tells you whether last year's customers are worth more or less this year, before you've spent a dollar on acquisition."
category: "Retention / NRR"
author: alain
date: "2026-05-21"
readTime: "7 min read"
deck: "Acquisition adds. Retention *multiplies*. Only one of those bends the long-run curve."
answer: "Net revenue retention compares what an existing customer cohort pays today with what it paid twelve months ago, ignoring anyone who joined since. Above 100 percent, the base grows on its own and compounds, while new logos only add linearly. Report it on revenue, hold the cohort fixed, and show gross retention beside it."
takeaways:
  - "NRR above 100 percent means the existing base grows without any new customers; a business at 120 percent roughly doubles its base every four years."
  - "New logos add revenue linearly; retained and expanding revenue compounds."
  - "Measure NRR on revenue, not logos, and keep the cohort fixed to avoid flattering the number."
  - "Gross retention is NRR with expansion removed, and it sets the floor of what you would keep if every upsell stopped."
---

Most growth dashboards lead with new bookings, because new bookings feel like progress
you can attribute to effort. But new logos add to revenue linearly. Net revenue
retention compounds it. A business at 120% NRR doubles its existing book roughly every
four years without signing a single new customer, and that quiet compounding is what
separates durable SaaS from the kind that has to run the acquisition treadmill forever.

## What NRR actually measures

Take a cohort of customers, look at their revenue today versus twelve months ago, and
ignore everyone who joined in between. Expansion and contraction within that group is
your net revenue retention. Above 100% means the cohort grew on its own. Below means
you're refilling a leaky bucket with every new sale.

- **Above 110%** — existing customers fund a meaningful share of growth.
- **Around 100%** — expansion just offsets churn; growth is entirely acquisition.
- **Below 100%** — you're losing ground on the base and acquisition is paying rent.

## Why it compounds and acquisition doesn't

A dollar of retained, expanding revenue shows up again next year, larger. A dollar of
acquisition spend buys a logo once. That's the whole asymmetry: NRR is the closest
thing a subscription business has to interest on a balance.

> You can outgrow a bad acquisition quarter. You cannot outgrow a base that shrinks
> every year.

## Reading NRR honestly

NRR is easy to flatter. Measuring it on revenue rather than logos, holding the cohort
fixed, and reporting gross retention alongside it keeps the number honest. Gross
retention sets the floor: it's NRR with expansion stripped out, and it tells you how
much you'd keep if every upsell stopped tomorrow.
